import { useTranslation } from "react-i18next";
import type { ReviewItem, ReviewStage, ReviewUrgency } from "./reviewSeed";

interface Props {
  items: readonly ReviewItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const STAGE_TONES: Record<ReviewStage, string> = {
  uploaded: "border-slate-200 bg-white text-slate-600",
  in_review: "border-blue-200 bg-blue-50 text-blue-600",
  approved: "border-emerald-400/40 bg-emerald-400/10 text-emerald-200",
  rejected: "border-rose-400/40 bg-rose-400/10 text-rose-200",
  promoted: "border-sky-400/40 bg-sky-400/10 text-sky-200",
};

const URGENCY_TONES: Record<ReviewUrgency, string> = {
  low: "text-slate-500",
  normal: "text-slate-600",
  high: "text-rose-300",
};

function relativeTime(iso: string, locale: string): string {
  const elapsedMinutes = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (Math.abs(elapsedMinutes) < 60) {
    return formatter.format(elapsedMinutes, "minute");
  }
  const elapsedHours = Math.round(elapsedMinutes / 60);
  if (Math.abs(elapsedHours) < 24) {
    return formatter.format(elapsedHours, "hour");
  }
  return formatter.format(Math.round(elapsedHours / 24), "day");
}

export function DocumentQueue({
  items,
  selectedId,
  onSelect,
}: Props): JSX.Element {
  const { t, i18n } = useTranslation();

  if (items.length === 0) {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
        <h3 className="text-sm font-semibold text-white">
          {t("dashboard.reviewConsole.queue.heading")}
        </h3>
        <p className="mt-2 text-sm text-slate-500">
          {t("dashboard.reviewConsole.queue.empty")}
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white">
      <header className="border-b border-slate-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-white">
          {t("dashboard.reviewConsole.queue.heading")}
        </h3>
        <p className="mt-0.5 text-xs text-slate-500">
          {t("dashboard.reviewConsole.queue.subheading")}
        </p>
      </header>
      <ul className="divide-y divide-white/5">
        {items.map((item) => {
          const selected = item.id === selectedId;
          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onSelect(item.id)}
                aria-pressed={selected}
                className={[
                  "grid w-full grid-cols-1 gap-2 px-4 py-3 text-left transition sm:grid-cols-[1fr_auto_auto_auto] sm:items-center sm:gap-3 sm:py-2.5",
                  selected
                    ? "bg-blue-50"
                    : "hover:bg-slate-50",
                ].join(" ")}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-white">
                    {item.title}
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    {item.subject}
                    {item.ownerName ? ` - ${item.ownerName}` : ""}
                  </p>
                </div>
                <span
                  className={[
                    "rounded-full border px-2 py-0.5 text-[11px] font-semibold",
                    STAGE_TONES[item.stage],
                  ].join(" ")}
                >
                  {t(`dashboard.reviewConsole.queue.stages.${item.stage}`)}
                </span>
                <span
                  className={[
                    "text-[11px] font-semibold uppercase tracking-wide",
                    URGENCY_TONES[item.urgency],
                  ].join(" ")}
                >
                  {t(`dashboard.reviewConsole.queue.urgency.${item.urgency}`)}
                </span>
                <span className="text-[11px] text-slate-500 sm:text-right">
                  {relativeTime(item.updatedAt, i18n.language)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
